import { apiClient } from '../client';

/**
 * The call page's qualification (ENG-2): pending for 3 s on the server, then
 * committed. See docs/api/errors.md, « Qualification d'un appel ».
 */

export type PadOutcome =
  | 'meeting_booked' | 'callback_requested' | 'interested' | 'not_interested'
  | 'gatekeeper' | 'left_voicemail' | 'do_not_call' | 'wrong_number';

export interface QualificationView {
  qualification_id: string;
  parallel_call_id: string;
  revision: number;
  state: 'pending' | 'committed' | 'cancelled';
  outcome: PadOutcome;
  commit_at: string;
  committed_at: string | null;
  /** After the commit: blacklist, crm_outbox; on a correction what could not be undone (meeting_manual_fix, crm_manual_fix). */
  effects?: string[];
}

export interface SubmitQualificationBody {
  qualification_id: string;
  outcome: PadOutcome;
  note?: string;
  follow_up_at?: string;
  timezone?: string;
  expected_revision?: number;
  suggested_outcome?: PadOutcome;
  suggestion_accepted?: boolean;
}

export function submitQualification(parallelCallId: string, body: SubmitQualificationBody, signal?: AbortSignal) {
  return apiClient.post<QualificationView>(`/calls/${encodeURIComponent(parallelCallId)}/qualifications`, body, { signal });
}

export function cancelQualification(qualificationId: string) {
  return apiClient.post<QualificationView>(`/qualifications/${encodeURIComponent(qualificationId)}/cancel`, {});
}

export function getQualification(qualificationId: string, signal?: AbortSignal) {
  return apiClient.get<QualificationView>(`/qualifications/${encodeURIComponent(qualificationId)}`, { signal });
}

export interface CallToReconcile {
  id: string;
  reason: 'live' | 'stale' | 'unqualified';
  call_status: string;
  call_outcome: string;
  prospect_name: string;
  prospect_phone: string;
  customer_id: string;
  created_date: string;
  updated_date: string;
}

export function reconcileCalls(signal?: AbortSignal) {
  return apiClient.get<{ items: CallToReconcile[]; truncated: boolean }>('/calls/reconcile', { signal });
}

export interface CurrentQualification {
  /** The revision that stands (neither cancelled nor failed); null: never qualified through this workflow. */
  current: QualificationView | null;
  /** 24 h after the first commit; null before any commit. */
  correctable_until: string | null;
}

/** « Corriger » from the history (W4): the revision to send, and the window. */
export function getCurrentQualification(parallelCallId: string, signal?: AbortSignal) {
  return apiClient.get<CurrentQualification>(`/calls/${encodeURIComponent(parallelCallId)}/qualifications/current`, { signal });
}
